/**
 * Transient confirmations.
 *
 * A toast is for something that happened, that the user caused, and that needs
 * no response. Anything the user must act on is an `Alert` on the page or a
 * `Dialog`: a message that disappears after five seconds is a message a
 * distracted person never saw, and this interface is used by people who are
 * distracted by their actual work.
 *
 * Built on Radix Toast, which supplies the part that is easy to get wrong: the
 * swipe gesture, the pause on hover and on focus, the `F8` hotkey that moves
 * focus into the toast region, and the live region that announces without
 * stealing focus.
 */

import { Toast as RadixToast } from 'radix-ui';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cx } from '~/lib/cx';
import { IconButton } from './Button';
import { AlertOctagon, CheckCircle, Close, Info } from './icons';

import styles from './Toast.module.css';

export type ToastTone = 'success' | 'danger' | 'info';

interface ToastRecord {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastApi {
  /** Show a toast. Returns its id, so a caller can dismiss it early. */
  show: (toast: { tone?: ToastTone; title: string; description?: string }) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Read the toast API.
 *
 * Throws outside the provider rather than returning a no-op, because a silent
 * no-op means a confirmation that never appears and nobody notices until a
 * user asks why nothing happened when they saved.
 */
export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside <ToastProvider>.');
  }
  return context;
}

const GLYPH = {
  success: CheckCircle,
  danger: AlertOctagon,
  info: Info,
} as const;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback<ToastApi['show']>((toast) => {
    const id = nextId.current++;
    setToasts((current) => [
      ...current,
      {
        id,
        tone: toast.tone ?? 'info',
        title: toast.title,
        ...(toast.description === undefined ? {} : { description: toast.description }),
      },
    ]);
    return id;
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {/* `swipeDirection` is right because the viewport is bottom-right; a
       * mismatch means the swipe does nothing and feels broken. */}
      <RadixToast.Provider swipeDirection="right" duration={6000}>
        {children}

        {toasts.map((toast) => {
          const Glyph = GLYPH[toast.tone];
          return (
            <RadixToast.Root
              key={toast.id}
              className={cx(styles.toast, styles[toast.tone])}
              // A failure stays until dismissed. A confirmation that is gone in
              // six seconds is fine; an error that is gone in six seconds is a
              // problem the user now cannot describe.
              duration={toast.tone === 'danger' ? Infinity : 6000}
              onOpenChange={(open) => {
                if (!open) dismiss(toast.id);
              }}
            >
              <Glyph size={16} className={styles.toastGlyph} />
              <div className={styles.toastBody}>
                <RadixToast.Title className={styles.toastTitle}>{toast.title}</RadixToast.Title>
                {toast.description ? (
                  <RadixToast.Description className={styles.toastDescription}>
                    {toast.description}
                  </RadixToast.Description>
                ) : null}
              </div>
              <RadixToast.Close asChild>
                <IconButton label="Dismiss" icon={<Close />} size="sm" />
              </RadixToast.Close>
            </RadixToast.Root>
          );
        })}

        <RadixToast.Viewport className={styles.viewport} label="Notifications" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}
